import { Component } from '@theme/component';

/**
 * @typedef {object} EyeCompareRefs
 * @property {HTMLElement} frame - Holds both illustrations; reads --compare.
 * @property {HTMLInputElement} range - Native range input that drives the split.
 */

/**
 * "Your phone photo ↔ finished piece" comparison in the Eye color finder. The range input moves the
 * split; the first time the comparison scrolls into view it sweeps left and right once (GSAP when the
 * atelier motion layer loaded it) to show that it can be dragged. Reduced motion skips the sweep.
 *
 * @extends Component<EyeCompareRefs>
 */
class EyeCompare extends Component {
  requiredRefs = ['frame', 'range'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {{ kill: () => void } | null} */
  #tween = null;

  #rafId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Any direct interaction stops the demo for good.
    for (const type of ['pointerdown', 'keydown', 'focusin']) {
      this.refs.range.addEventListener(type, () => this.#stopDemo(), { signal });
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#playDemo();
      },
      { threshold: 0.6 }
    );
    this.#observer.observe(this.refs.frame);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
    this.#stopDemo();
  }

  sync() {
    this.#setSplit(Number(this.refs.range.value));
  }

  /** @param {number} value - 0–100 */
  #setSplit(value) {
    this.refs.frame.style.setProperty('--compare', `${value}%`);
  }

  #playDemo() {
    const { range } = this.refs;
    const state = { value: Number(range.value) || 50 };
    const apply = () => {
      range.value = String(Math.round(state.value));
      this.#setSplit(state.value);
    };

    const gsap = /** @type {any} */ (window).gsap;
    if (gsap) {
      this.#tween = gsap
        .timeline({ delay: 0.3 })
        .to(state, { value: 22, duration: 0.9, ease: 'power2.inOut', onUpdate: apply })
        .to(state, { value: 78, duration: 1.2, ease: 'power2.inOut', onUpdate: apply })
        .to(state, { value: 50, duration: 0.8, ease: 'power2.inOut', onUpdate: apply });
      return;
    }

    // Fallback: the same sweep with requestAnimationFrame.
    const keyframes = [50, 22, 78, 50];
    const durations = [900, 1200, 800];
    let segment = 0;
    let start = performance.now();
    const ease = (/** @type {number} */ p) => (p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2);
    const step = (/** @type {number} */ now) => {
      const duration = durations[segment] ?? 0;
      const progress = Math.min(1, (now - start) / duration);
      const from = keyframes[segment] ?? 50;
      const to = keyframes[segment + 1] ?? 50;
      state.value = from + (to - from) * ease(progress);
      apply();
      if (progress >= 1) {
        segment += 1;
        start = now;
        if (segment >= durations.length) return;
      }
      this.#rafId = requestAnimationFrame(step);
    };
    this.#rafId = requestAnimationFrame(step);
  }

  #stopDemo() {
    this.#tween?.kill();
    this.#tween = null;
    cancelAnimationFrame(this.#rafId);
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('eye-compare-component')) {
  customElements.define('eye-compare-component', EyeCompare);
}
